/**
 * Unified Diagram Controls - Filter Dropdown Component
 * Issue #245: Standardize Diagram Controls Across All Renderers
 *
 * A dropdown-based filter component for selecting filter values.
 */

import { memo, useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';
import type { FilterDefinition } from './types';

interface FilterDropdownProps {
  filter: FilterDefinition;
  activeValues: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
  className?: string;
}

/**
 * Filter dropdown component for selecting filter values
 */
export const FilterDropdown = memo(function FilterDropdown({
  filter,
  activeValues,
  onToggle,
  onClear,
  className = '',
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasActiveFilters = activeValues.size > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get display label
  const displayLabel = hasActiveFilters
    ? `${filter.label} (${activeValues.size})`
    : filter.label;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-all
          ${hasActiveFilters
            ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300'
            : 'bg-white border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300'
          }
          hover:bg-slate-50 dark:hover:bg-slate-700
        `}
      >
        <span>{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        {hasActiveFilters && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="ml-1 p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800 rounded"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 min-w-[180px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg py-1">
          {filter.options.map((option) => {
            const isActive = activeValues.has(option.value);

            return (
              <button
                key={option.value}
                onClick={() => onToggle(option.value)}
                className={`
                  w-full flex items-center gap-2 px-3 py-2 text-sm text-left
                  ${isActive
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                  }
                `}
              >
                <div
                  className={`
                    w-4 h-4 rounded border flex items-center justify-center
                    ${isActive
                      ? 'bg-blue-500 border-blue-500'
                      : 'border-slate-300 dark:border-slate-600'
                    }
                  `}
                >
                  {isActive && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex items-center gap-2 flex-1">
                  {option.color && (
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <span>{option.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
